import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  TextInput,
} from "react-native";

import { Calendar } from "react-native-calendars";
import { useTranslation } from "react-i18next";
import { FontAwesome } from "@expo/vector-icons";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";

import { observer, Observer } from "mobx-react";

import { Feather } from "@expo/vector-icons";
import { useScreenDimensions, useDevice } from "../../_layout";
import IconEmptyView from "../../Components/IconEmptyView";
import SearchBar from "../../Components/SearchBar";
import ClientWeekStatusText from "../../Components/ClientWeekStatusText";
import CustomAlert from "../../Components/CustomAlert";
import FloatingMenu from "../../Components/FloatingMenu";
import {
  getInitialCompetitionFederationSuggestions,
  getWeightClasses,
  hasFederationFamilyChanged,
  mapWeightClass,
  remapSelectedClientsWeightClasses,
} from "./competitionFederationConfig";
import CompetitionFederationSearchMenu from "./CompetitionFederationSearchMenu";

const AddCompetitionView = ({ isVisible, onClose, competition, coach }) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const [showingFederationChangeAlert, setShowingFederationChangeAlert] =
    useState(false);
  const [pendingCompetitionLevel, setPendingCompetitionLevel] = useState("");
  const [competitionFloatingPortalRoot, setCompetitionFloatingPortalRoot] =
    useState(null);
  const handleCompetitionFloatingPortalHostRef = useCallback((node) => {
    setCompetitionFloatingPortalRoot((current) =>
      current === node ? current : node || null,
    );
  }, []);

  useEffect(() => {
    coach.initAddCompetitionView(competition);
  }, [coach]);

  const Cancel = () => {
    coach.resetAuxCompetition();
    if (competition) {
      competition.setProperty("isSelected", false);
    }
    onClose();

    coach.setSearchTerm("");
    onClose();
  };

  const competitionClasses = ["Subjunior", "Junior", "Open", "Master"];

  const canSaveCompetition = () =>
    coach.competitionName !== "" &&
    coach.competitionCategory !== "" &&
    coach.competitionLevel !== "";

  const handleSave = async () => {
    if (!canSaveCompetition()) {
      return;
    }

    coach.addOrEditCompetition(competition);
    onClose();
  };
  const applyCompetitionLevelChange = (
    nextCompetitionLevel,
    skipConfirmation = false
  ) => {
    const previousLevel = coach.competitionLevel;
    const shouldChangeFamily = hasFederationFamilyChanged(
      previousLevel,
      nextCompetitionLevel
    );

    const hasSelectedWeightClasses = coach.competitionSelectedClients.some(
      (client) => !!client.selectedWeightClass
    );

    if (
      shouldChangeFamily &&
      hasSelectedWeightClasses &&
      skipConfirmation !== true
    ) {
      setPendingCompetitionLevel(nextCompetitionLevel);
      setShowingFederationChangeAlert(true);
      return false;
    }

    if (shouldChangeFamily) {
      remapSelectedClientsWeightClasses(
        coach.competitionSelectedClients,
        previousLevel,
        nextCompetitionLevel
      );

      if (coach.selectedWeightClass && coach.selectedWeightClassGender != null) {
        coach.setProperty(
          "selectedWeightClass",
          mapWeightClass(
            coach.selectedWeightClass,
            previousLevel,
            nextCompetitionLevel,
            coach.selectedWeightClassGender
          )
        );
      }

    }

    coach.setProperty("competitionLevel", nextCompetitionLevel);
    return true;
  };

  const year = new Date().getFullYear();
  const month = new Date().getMonth();
  const day = new Date().getDate();
  const today = new Date(year, month, day, 12);

  const { screenSize } = useScreenDimensions();

  const ClientsList = observer(() => {
    const selectedWeightClassGender = coach.selectedWeightClassGender;

    const ClientRow = observer(({ client, index }) => {
      const length = coach
        .getFilteredCompetitionClients(selectedWeightClassGender)
        .filter((client) => client.status === "active-string").length;
      const select = () => {
        //COPY THE COMP IN THE CLIENT WITH THE SELECTED WEIGHT CLASS
        // client.addCompetition(competition, coach.selectedWeightClass);
        // client.updateClientTransactionCompetitions(competition);
        client.setProperty("selectedWeightClass", coach.selectedWeightClass);
        coach.competitionSelectedClients.push(client);
        coach.setSearchTerm("");
      };

      const [isHovered, setIsHovered] = useState(false);

      return (
        client && (
          <View
            style={{
              flexDirection: "row",
              flex: 1,
              paddingVertical: 4,
              alignItems: "center",
              backgroundColor: isHovered
                ? "rgba(63, 123, 141, 0.05)"
                : "transparent",
              paddingVertical: 8,
              borderBottomLeftRadius: index === length - 1 ? 12 : 0,
              borderBottomRightRadius: index === length - 1 ? 12 : 0,
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <TouchableOpacity onPress={select} style={{ flex: 1 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <View
                  style={{
                    alignItems: "flex-start",
                    width: "100%",
                    paddingHorizontal: 10,
                  }}
                >
                  <Text
                    style={{
                      color: Colors.blackColor,
                      fontSize: 14,
                      flexShrink: 1,
                    }}
                  >
                    {client.nameForCoach} {client.surnameForCoach}
                  </Text>
                  <ClientWeekStatusText
                    client={client}
                    style={{
                      color: Colors.grayColor,
                      fontSize: 12,
                      marginTop: 2,
                    }}
                    showColorForUnprepared={true}
                  />
                </View>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                }}
              />
              <View
                style={{
                  backgroundColor: Colors.grayColor,
                }}
              />
            </TouchableOpacity>
          </View>
        )
      );
    });

    return (
      <View
        style={{
          width: screenSize != "large" ? "50%" : "33%",
          paddingHorizontal: screenSize != "large" ? 0 : 16,
          paddingLeft: screenSize != "large" ? 8 : 16,
        }}
      >
        {coach.selectedWeightClass === "" ? (
          <IconEmptyView
            iconLibrary={"Ionicons"}
            mainText={"emptyCategoryTitle-string"}
            subText={"emptyCategoryText-string"}
            iconName={"body-outline"}
            marginTop={screenSize === "large" ? 0 : 40}
          />
        ) : (
          <>
            <View
              style={{
                marginTop: screenSize != "large" ? 8 : 16,
                width: "100%",
              }}
            >
              <SearchBar
                text={coach.searchTerm}
                onChangeText={(text) => coach.setSearchTerm(text)}
                dark={false}
                placeholder={"searchClient-string"}
                maxWidth={300}
              />

              <Text
                style={{
                  color: Colors.grayColor,
                  fontSize: 12,
                  marginBottom: 4,
                  marginTop: 12,
                }}
              >
                {t("athletes-string")}
              </Text>
              <View
                style={{
                  height: 0.5,
                  backgroundColor: Colors.grayColor,
                }}
              />
            </View>
            <Observer>
              {() => (
                <ScrollView
                  contentContainerStyle={{ height: "100%" }}
                  showsVerticalScrollIndicator={false}
                >
                  {coach &&
                  coach
                    .getFilteredCompetitionClients(selectedWeightClassGender)
                    .filter((client) => client.status === "active-string")
                    .length > 0 ? (
                    <>
                      {coach
                        .getFilteredCompetitionClients(
                          selectedWeightClassGender
                        )
                        .filter((client) => client.status === "active-string")
                        .map((client, index) => (
                          <View key={client.id}>
                            <ClientRow client={client} index={index} />
                            {index <
                              coach
                                .getFilteredCompetitionClients(
                                  selectedWeightClassGender
                                )
                                .filter(
                                  (client) => client.status === "active-string"
                                ).length -
                                1 && (
                              <View
                                style={{
                                  height: 0.5,
                                  backgroundColor: Colors.grayColor,
                                }}
                              />
                            )}
                          </View>
                        ))}
                    </>
                  ) : (
                    <IconEmptyView
                      iconLibrary={"Ionicons"}
                      mainText={"emptyClientsTitle-string"}
                      subText={"noAthletesFoundText-string"}
                      iconName={"people-outline"}
                      marginTop={screenSize === "large" ? 0 : 44}
                    />
                  )}
                </ScrollView>
              )}
            </Observer>
          </>
        )}
      </View>
    );
  });

  const HeaderButtons = () => {
    const Buttons = observer(({ bool }) => {
      const text = bool ? "save-string" : "cancel-string";
      const [isHovered, setIsHovered] = useState(false);
      const isSaveEnabled = bool ? canSaveCompetition() : true;

      return bool ? (
        <TouchableOpacity
          onPress={handleSave}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              // marginHorizontal: 20,
              backgroundColor: Colors.bubbleColor,
              borderRadius: 14,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: Colors.bubbleColor,
              width: 80,
              height: 35,
              justifyContent: "center",
              alignItems: "center",
              opacity: isSaveEnabled ? (isHovered ? 0.7 : 1) : 0.5,
            }}
          >
            <Text
              style={{
                textAlignVertical: "center",
                textAlign: "center",
                color: Colors.whiteColor,
              }}
            >
              {t(text)}
            </Text>
          </View>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          onPress={Cancel}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              backgroundColor: isHovered ? Colors.redColor : "transparent",
              borderRadius: 14,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: "red",
              width: 80,
              height: 35,
              justifyContent: "center",
              alignItems: "center",

              marginHorizontal: 10,
            }}
          >
            <Text
              style={{
                textAlignVertical: "center",
                textAlign: "center",
                color: isHovered ? Colors.whiteColor : "red",
              }}
            >
              {t(text)}
            </Text>
          </View>
        </TouchableOpacity>
      );
    });
    return (
      <View
        style={{
          flexDirection: "row",
          width: "100%",
          justifyContent: "flex-end",
          // paddingHorizontal: 20,
          // marginTop: 20,
          marginBottom: screenSize != "large" ? 16 : 16,
        }}
      >
        <Buttons bool={false} />
        <Buttons bool={true} />
      </View>
    );
  };

  const CompetitionInfo = observer(() => {
    const CalendarView = ({ today }) => {
      const [currentMonth, setCurrentMonth] = useState(new Date());

      const CustomCalendarHeader = ({ currentMonth, onMonthChange }) => {
        const year = currentMonth.getFullYear();

        function capitalizeFirstLetter(string) {
          return string.charAt(0).toUpperCase() + string.slice(1);
        }
        const monthName = capitalizeFirstLetter(
          currentMonth.toLocaleString(coach.coachLanguage, {
            month: "long",
          })
        );

        const ArrowButton = ({ bool }) => {
          return (
            <TouchableOpacity
              onPress={() => (bool ? onMonthChange(1) : onMonthChange(-1))}
            >
              <FontAwesome
                name={bool ? "chevron-right" : "chevron-left"}
                size={30}
                color={Colors.bubbleColor}
              />
            </TouchableOpacity>
          );
        };

        return (
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ArrowButton bool={false} />

            <View
              style={{
                width: "60%",
                alignItems: "center",
              }}
            >
              <Text style={{ fontSize: 16, fontWeight: "bold" }}>
                {monthName}
              </Text>
              <Text style={{ textAlign: "center", fontSize: 12 }}>{year}</Text>
            </View>

            <ArrowButton bool={true} />
          </View>
        );
      };
      const CustomDayHeader = () => {
        return (
          <View
            style={{
              flexDirection: "row",
              marginTop: 5,
              backgroundColor: "white",
            }}
          >
            {[
              "monHeader-string",
              "tueHeader-string",
              "wedHeader-string",
              "thuHeader-string",
              "friHeader-string",
              "satHeader-string",
              "sunHeader-string",
            ].map((day) => (
              <Text
                key={day}
                style={{
                  color: "black",
                  fontWeight: "bold",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {t(day)}
              </Text>
            ))}
          </View>
        );
      };
      const handleMonthChange = (offset) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(currentMonth.getMonth() + offset);
        setCurrentMonth(newMonth);
      };
      const isSameDay = (date1, date2) => {
        return (
          date1.getDate() === date2.getDate() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getFullYear() === date2.getFullYear()
        );
      };
      const handleDayPress = (day) => {
        // setSelected(new Date(day.year, day.month - 1, day.day, 12));
        coach.setProperty(
          "competitionDate",
          new Date(day.year, day.month - 1, day.day, 12)
        );
      };
      const CardView = observer(({ date }) => {
        const isSameMonth = date.month === currentMonth.getMonth() + 1;
        const isSelected = isSameDay(
          coach.competitionDate,
          new Date(date.year, date.month - 1, date.day, 12)
        );
        const isToday = isSameDay(
          today,
          new Date(date.year, date.month - 1, date.day)
        );

        const [isHovered, setIsHovered] = useState(false);

        return (
          <View
            style={{
              height: 30,
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <TouchableOpacity
              onPress={() => {
                // isSameMonth ? handleDayPress(date) : console.log("miau");
                handleDayPress(date);
              }}
              style={{
                height: 30,
                width: 30,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  justifyContent: "flex-start",
                  height: 30,
                  width: 30,
                  backgroundColor: isHovered
                    ? "rgba(63, 123, 141, 0.1)"
                    : isSelected
                    ? Colors.bubbleColor
                    : "white",
                  borderRadius: 50,
                  justifyContent: "center",
                  alignItems: "center",
                  // marginTop: -5,
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Text
                  style={{
                    textAlign: "center",
                    color: isSelected
                      ? "white"
                      : isSameMonth
                      ? isToday
                        ? "#3F7B8D"
                        : "black"
                      : "gray",
                    fontWeight: isSameMonth
                      ? isToday
                        ? "900"
                        : "600"
                      : "normal",
                    opacity: isSameMonth ? 1 : 0.5,
                    fontSize: 14,
                    // paddingTop: 8,
                    // paddingBottom: 16,
                  }}
                >
                  {date.day}
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        );
      });
      return (
        <View
          style={{
            width: "100%",
            marginTop: screenSize != "large" ? 16 : 24,
            minWidth: 220,
          }}
        >
          <CustomCalendarHeader
            currentMonth={currentMonth}
            onMonthChange={handleMonthChange}
          />
          <CustomDayHeader />
          <Calendar
            firstDay={1}
            key={currentMonth.getMonth() + currentMonth.getFullYear()}
            style={{ width: "100%", padding: 0 }}
            current={currentMonth.toISOString().split("T")[0]}
            dayComponent={({ date, state }) => {
              return <CardView date={date} />;
            }}
            hideArrows={true}
            renderHeader={(date) => null}
            hideDayNames
            theme={{
              backgroundColor: "white", // background color for the calendar
              calendarBackground: "white", // background color for the day
              monthTextColor: "black", // color for the month's name
              textMonthFontSize: 25, // Adjust as needed (optional)
              textDayHeaderFontSize: 15,
              textSectionTitleColor: "black",
              textDayHeaderFontWeight: "700",
            }}
          />
        </View>
      );
    };

    const CompInfoPickers = observer(() => {
      const initialFederationSuggestions = getInitialCompetitionFederationSuggestions(
        (coach?.competitions ?? [])
          .filter((existingCompetition) => existingCompetition?.id !== competition?.id)
          .map((existingCompetition) => existingCompetition?.competitionLevel)
      );

      const selectClass = (competitionClass) => {
        coach.setProperty("competitionCategory", competitionClass);
      };

      const selectLevel = (competitionLevel) => {
        const hasChanged = applyCompetitionLevelChange(competitionLevel);
        if (hasChanged) {
          coach.setProperty("showingCustomLevel", false);
        }
      };

      const FloatingFieldTrigger = ({ value, placeholder }) => {
        const [isHovered, setIsHovered] = useState(false);

        return (
          <View
            style={{
              width: "100%",
              height: 34,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: isHovered
                ? "rgba(0, 0, 0, 0.5)"
                : Colors.borderColor,
              paddingHorizontal: 10,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: Colors.whiteColor,
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Text
              style={{
                fontSize: 13,
                color: value ? Colors.blackColor : Colors.grayColor,
                flex: 1,
                marginRight: 8,
              }}
              numberOfLines={1}
            >
              {value || placeholder}
            </Text>
            <Feather name="chevron-down" size={18} color={Colors.grayColor} />
          </View>
        );
      };

      return (
        <View style={{ width: "100%" }}>
          <View
            style={{
              width: "100%",
              flexDirection: "row",
              justifyContent: "space-between",
              marginTop: screenSize != "large" ? 8 : 12,
            }}
          >
            <View
              style={{
                width: "50%",
                paddingRight: screenSize != "large" ? 4 : 6,
              }}
            >
              <FloatingMenu
                placement="bottom-start"
                width={180}
                maxHeight={220}
                items={competitionClasses}
                portalRoot={competitionFloatingPortalRoot}
                captureKeyboardWhenOpen={true}
                trigger={
                  <FloatingFieldTrigger
                    value={coach.competitionCategory}
                    placeholder={t("category-string")}
                  />
                }
                renderItem={({ item, close }) => (
                  <TouchableOpacity
                    onPress={() => {
                      selectClass(item);
                      close?.();
                    }}
                    style={{
                      width: "100%",
                      paddingVertical: 10,
                      paddingHorizontal: 12,
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 13,
                        color:
                          coach.competitionCategory === item
                            ? Colors.bubbleColor
                            : Colors.blackColor,
                        fontWeight:
                          coach.competitionCategory === item ? "600" : "400",
                      }}
                    >
                      {item}
                    </Text>
                    {coach.competitionCategory === item && (
                      <Feather
                        name="check"
                        size={15}
                        color={Colors.bubbleColor}
                      />
                    )}
                  </TouchableOpacity>
                )}
              />
            </View>
            <View
              style={{
                width: "50%",
                paddingLeft: screenSize != "large" ? 4 : 6,
              }}
            >
              <CompetitionFederationSearchMenu
                value={coach.competitionLevel}
                onSelect={selectLevel}
                triggerLabel={t("federation-string")}
                searchPlaceholder={t("searchFederation-string")}
                emptyLabel={t("noFederationsFound-string")}
                suggestionsLabel={t("suggestions-string")}
                initialSuggestions={initialFederationSuggestions}
                portalRoot={competitionFloatingPortalRoot}
              />
            </View>
          </View>
        </View>
      );
    });

    const NameText = observer(() => {
      const handleChange = (text) => {
        coach.setProperty("competitionName", text);
      };
      const [isHovered, setIsHovered] = useState(false);

      return (
        <TextInput
          style={[
            GlobalStyles.textInputStyle,
            {
              borderRadius: 10,
              borderWidth: 1,
              borderColor: isHovered
                ? "rgba(0, 0, 0, 0.5)"
                : Colors.borderColor,
              padding: 8,
              marginTop: screenSize != "large" ? 0 : 16,
            },
          ]}
          placeholder={t("nameOfCompetition-string")}
          placeholderTextColor={Colors.grayColor}
          value={coach.competitionName}
          onChangeText={handleChange}
          maxLength={40}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
      );
    });
    return (
      <View
        style={{
          width: screenSize != "large" ? "100%" : "33%",
          alignItems: "center",
          paddingHorizontal: screenSize != "large" ? 0 : 16,
          alignSelf: screenSize === "large" ? "center" : "auto",
        }}
      >
        <NameText />
        <CompInfoPickers />
        <CalendarView today={today} />
      </View>
    );
  });

  const CategoriesList = observer(() => {
    const femaleWeightClasses = getWeightClasses(coach.competitionLevel, true);
    const maleWeightClasses = getWeightClasses(coach.competitionLevel, false);

    const CategoryLayoutView = observer(({ title, gender }) => {
      const ClientButton = observer(({ client, index }) => {
        const select = () => {
          coach.setProperty(
            "competitionSelectedClients",
            coach.competitionSelectedClients.filter((c) => c.id !== client.id)
          );
        };
        const [isHovered, setIsHovered] = useState(false);

        return (
          <TouchableOpacity
            onPress={select}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                styles.AttributeButton,
                {
                  backgroundColor: isHovered
                    ? "rgba(240, 68, 56, 0.8)"
                    : "transparent",
                  borderColor: isHovered
                    ? "rgba(240, 68, 56, 0.8)"
                    : Colors.grayColor,
                },
              ]}
            >
              <View style={[GlobalStyles.HStack, { alignItems: "center" }]}>
                <Text
                  key={index}
                  style={{
                    fontSize: 12,
                    padding: 5,
                    color: isHovered ? "white" : Colors.grayColor,
                  }}
                >
                  {client.nameForCoach} {client.surnameForCoach}
                </Text>
              </View>
            </View>
          </TouchableOpacity>
        );
      });

      const CategoryButton = observer(() => {
        const [isHovered, setIsHovered] = useState(false);

        return (
          <TouchableOpacity
            onPress={() => {
              coach.setProperty("selectedWeightClass", title);
              coach.setProperty("selectedWeightClassGender", gender);
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Text
              style={[
                coach.selectedWeightClass === title &&
                coach.selectedWeightClassGender === gender
                  ? Fonts.medium16
                  : Fonts.regular14,
                {
                  fontWeight:
                    isHovered ||
                    (coach.selectedWeightClass === title &&
                      coach.selectedWeightClassGender === gender)
                      ? "600"
                      : "normal",
                },
              ]}
            >
              {title}
            </Text>
          </TouchableOpacity>
        );
      });

      return (
        <View
          style={{
            width: "100%",
          }}
        >
          <View
            style={[GlobalStyles.HStack, { justifyContent: "space-between" }]}
          >
            <CategoryButton />
          </View>
          <View style={styles.AttributeFlex}>
            {coach
              .clientsInWeightClass(null, title, gender)
              .map((client, index) => {
                return (
                  <ClientButton key={client.id} client={client} index={index} />
                );
              })}
          </View>
        </View>
      );
    });
    return (
      <View
        style={{
          width: screenSize != "large" ? "50%" : "33%",
          paddingHorizontal: screenSize != "large" ? 0 : 16,
          marginTop: screenSize != "large" ? 8 : 0,
          marginBottom: screenSize != "large" ? 8 : 0,
          borderRightWidth: screenSize != "large" ? 1 : 0,
          borderColor: "rgba(0,0,0,0.1)",
        }}
      >
        <Observer>
          {() => (
            <ScrollView
              style={{
                marginHorizontal: screenSize != "large" ? 4 : 0,
              }}
              contentContainerStyle={{ height: "100%" }}
              showsVerticalScrollIndicator={false}
            >
              <Text
                style={[
                  Fonts.medium14,
                  {
                    marginBottom: 10,
                    marginTop: screenSize != "large" ? 0 : 16,
                  },
                ]}
              >
                {t("women-string")}
              </Text>

              {femaleWeightClasses.map((weightClass) => (
                <CategoryLayoutView
                  key={weightClass}
                  title={weightClass}
                  gender={true}
                />
              ))}

              <Text
                style={[Fonts.medium14, { marginTop: 10, marginBottom: 10 }]}
              >
                {t("men-string")}
              </Text>

              {maleWeightClasses.map((weightClass) => (
                <CategoryLayoutView
                  key={weightClass}
                  title={weightClass}
                  gender={false}
                />
              ))}
            </ScrollView>
          )}
        </Observer>
      </View>
    );
  });

  return (
    <>
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        // onRequestClose={onClose}
      >
        <View
          ref={handleCompetitionFloatingPortalHostRef}
          collapsable={false}
          dataSet={{ efortcoachFloatingRoot: "true" }}
          style={GlobalStyles.centeredView}
        >
          <View style={GlobalStyles.touchableBackground} />
          <View
            style={[
              styles.modalView,
              {
                position: "absolute",
                maxHeight: "90%",
                minHeight: screenSize != "large" ? 200 : 500,
                maxWidth: "90%",
                padding: screenSize === "small" ? 8 : 24,
                width: screenSize != "large" ? 500 : "auto",
              },
            ]}
            onStartShouldSetResponder={() => false}
          >
            <HeaderButtons />
            {screenSize != "large" ? (
              <ScrollView
                style={{
                  flex: 1,
                  flexDirection: screenSize != "large" ? "column" : "row",
                  borderWidth: screenSize != "large" ? 0 : 1,
                  borderRadius: screenSize != "large" ? 0 : 20,
                  borderColor: "rgba(0,0,0,0.1)",
                  maxWidth: screenSize != "large" ? 500 : 1000,
                }}
                contentContainerStyle={{
                  height: "100%",
                }}
                showsVerticalScrollIndicator={false}
              >
                <View
                  style={{
                    height: "100%",
                  }}
                >
                  <CompetitionInfo />

                  <View
                    style={[
                      {
                        height: "100%",
                        flex: 1,
                        flexDirection: "row",
                        alignItems: "flex-start",
                      },
                    ]}
                  >
                    <CategoriesList />
                    <ClientsList />
                  </View>
                </View>
              </ScrollView>
            ) : (
              <View
                style={{
                  flexDirection: screenSize != "large" ? "column" : "row",
                  flex: 1,
                  minWidth: 900,
                  height: "100%",
                  borderRadius: 16,
                  borderWidth: 0.1,
                  borderColor: Colors.borderColor,
                  justifyContent: "space-between",
                }}
              >
                <CompetitionInfo />
                <View
                  style={{
                    width: 1,
                    alignSelf: "center",
                    // height: "100%",
                    height: 400,
                    backgroundColor: "rgba(0,0,0,0.1)",
                  }}
                />

                <CategoriesList />
                <View
                  style={{
                    width: 1,
                    alignSelf: "center",
                    height: 400,
                    backgroundColor: "rgba(0,0,0,0.1)",
                  }}
                />
                <ClientsList />
              </View>
            )}
          </View>
        </View>
      </Modal>
      {showingFederationChangeAlert && (
        <CustomAlert
          isVisible={showingFederationChangeAlert}
          onClose={() => {
            setShowingFederationChangeAlert(false);
            setPendingCompetitionLevel("");
          }}
          title={"competitionFederationChangeAlertTitle-string"}
          text={"competitionFederationChangeAlertText-string"}
          action={async () => {
            applyCompetitionLevelChange(pendingCompetitionLevel, true);
            coach.setProperty("showingCustomLevel", false);
          }}
          specialCTA={"continue-string"}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  modalView: {
    borderRadius: 18,
    backgroundColor: Colors.whiteColor,
  },
  AttributeFlex: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    marginVertical: 10,
  },
  AttributeButton: {
    marginVertical: 5,
    marginRight: 5,
    borderRadius: 8,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
});

export default AddCompetitionView;
